import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppDispatch, AppThunk } from "@/app/store";
import {
  CustomersAverageInvoiceComparisonData,
  ExistingCustomerReplacementEfficiencyDataItem,
  ExistingCustomerReplacementEfficiencyByMonthData,
  FetchDashboardDataRequest,
  FetchDashboardDataResponse,
  LastSyncInfo,
  LifetimeValueByTierData,
  RevenueByInvoiceAgeDataItem,
  NewCustomerReplacementEfficiencyDataItem,
  NewCustomerReplacementEfficiencyByMonthData,
  PercentageOfNewCustomersByZipCodeData,
  PercentageOfNewCustomersChangeByZipCodeDataItem,
  TotalSalesByYearAndMonthData,
  TotalSalesByZipCodeDataItem,
  TotalHouseholdsByZipCodeData,
  TotalSalesNewCustomerByZipCodeAndYearData,
  TotalSalesNewVsExistingCustomerDataItem,
} from "@/modules/stochastic/features/Reports/SCDashboard/api/fetchStochasticDashboardData/types";
import { fetchStochasticDashboardData } from "@/modules/stochastic/features/Reports/SCDashboard/api/fetchStochasticDashboardData/fetchStochasticDashboardDataApi";
import { refreshStochasticDashboardData } from "@/modules/stochastic/features/Reports/SCDashboard/api/refreshStochasticDashboardData/refreshStochasticDashboardDataApi";

interface StochasticDashboardState {
  dashboardData: {
    revenueByInvoiceAgeData: RevenueByInvoiceAgeDataItem[];
    lifetimeValueByTierData: LifetimeValueByTierData;
    totalSalesByZipCodeData: TotalSalesByZipCodeDataItem[];
    totalHouseholdsByZipCodeData: TotalHouseholdsByZipCodeData;
    totalSalesByYearAndMonthData: TotalSalesByYearAndMonthData;
    customersAverageInvoiceComparisonData: CustomersAverageInvoiceComparisonData;
    percentageOfNewCustomersByZipCodeData: PercentageOfNewCustomersByZipCodeData;
    totalSalesNewVsExistingCustomerData: TotalSalesNewVsExistingCustomerDataItem[];
    newCustomerReplacementEfficiencyData: NewCustomerReplacementEfficiencyDataItem[];
    newCustomerReplacementEfficiencyByMonthData: NewCustomerReplacementEfficiencyByMonthData;
    existingCustomerReplacementEfficiencyData: ExistingCustomerReplacementEfficiencyDataItem[];
    existingCustomerReplacementEfficiencyByMonthData: ExistingCustomerReplacementEfficiencyByMonthData;
    totalSalesNewCustomerByZipCodeAndYearData: TotalSalesNewCustomerByZipCodeAndYearData;
    percentageOfNewCustomersChangeByZipCodeData: PercentageOfNewCustomersChangeByZipCodeDataItem[];
    lastSync: LastSyncInfo | null;
  };
  loading: boolean;
  refreshing: boolean;
  error: string | null;
}

const DEFAULT_CUSTOMERS_AVERAGE_INVOICE_COMPARISON_DATA: CustomersAverageInvoiceComparisonData =
  {
    chartData: [],
    avgSales: {
      newCustomerAvg: 0,
      repeatCustomerAvg: 0,
    },
  };

const DEFAULT_LIFETIME_VALUE_BY_TIER_DATA: LifetimeValueByTierData = {
  chartData: [],
  totalHouseholdsCount: 0,
};

const DEFAULT_TOTAL_HOUSEHOLDS_BY_ZIP_CODE_DATA: TotalHouseholdsByZipCodeData =
  {
    items: [],
    avgNewCustomerHouseholdValue: 0,
    totalNewCustomerHouseholds: 0,
    totalZipCodes: 0,
    availableZipCodes: [],
  };

const DEFAULT_PERCENTAGE_OF_NEW_CUSTOMERS_BY_ZIP_CODE_DATA: PercentageOfNewCustomersByZipCodeData =
  {
    years: [],
    totalCount: 0,
    minAvgPercentage: 0,
    maxAvgPercentage: 0,
    items: [],
  };

const DEFAULT_TOTAL_SALES_BY_YEAR_AND_MONTH_DATA: TotalSalesByYearAndMonthData =
  {
    all: { years: [], items: [] },
    new: { years: [], items: [] },
    existing: { years: [], items: [] },
  };

const DEFAULT_TOTAL_SALES_NEW_CUSTOMER_BY_ZIP_CODE_AND_YEAR_DATA: TotalSalesNewCustomerByZipCodeAndYearData =
  {
    years: [],
    totalCount: 0,
    minAvgSales: 0,
    maxAvgSales: 0,
    items: [],
  };

const DEFAULT_NEW_CUSTOMER_REPLACEMENT_EFFICIENCY_BY_MONTH_DATA: NewCustomerReplacementEfficiencyByMonthData =
  {
    years: [],
    items: [],
  };

const DEFAULT_EXISTING_CUSTOMER_REPLACEMENT_EFFICIENCY_BY_MONTH_DATA: ExistingCustomerReplacementEfficiencyByMonthData =
  {
    years: [],
    items: [],
  };

const initialState: StochasticDashboardState = {
  dashboardData: {
    revenueByInvoiceAgeData: [],
    lifetimeValueByTierData: DEFAULT_LIFETIME_VALUE_BY_TIER_DATA,
    totalSalesByZipCodeData: [],
    totalHouseholdsByZipCodeData: DEFAULT_TOTAL_HOUSEHOLDS_BY_ZIP_CODE_DATA,
    totalSalesByYearAndMonthData: DEFAULT_TOTAL_SALES_BY_YEAR_AND_MONTH_DATA,
    customersAverageInvoiceComparisonData:
      DEFAULT_CUSTOMERS_AVERAGE_INVOICE_COMPARISON_DATA,
    percentageOfNewCustomersByZipCodeData:
      DEFAULT_PERCENTAGE_OF_NEW_CUSTOMERS_BY_ZIP_CODE_DATA,
    totalSalesNewVsExistingCustomerData: [],
    newCustomerReplacementEfficiencyData: [],
    newCustomerReplacementEfficiencyByMonthData:
      DEFAULT_NEW_CUSTOMER_REPLACEMENT_EFFICIENCY_BY_MONTH_DATA,
    existingCustomerReplacementEfficiencyData: [],
    existingCustomerReplacementEfficiencyByMonthData:
      DEFAULT_EXISTING_CUSTOMER_REPLACEMENT_EFFICIENCY_BY_MONTH_DATA,
    totalSalesNewCustomerByZipCodeAndYearData:
      DEFAULT_TOTAL_SALES_NEW_CUSTOMER_BY_ZIP_CODE_AND_YEAR_DATA,
    percentageOfNewCustomersChangeByZipCodeData: [],
    lastSync: null,
  },
  loading: false,
  refreshing: false,
  error: null,
};

const stochasticDashboardSlice = createSlice({
  name: "stochasticDashboard",
  initialState,
  reducers: {
    setChartsData: (
      state,
      action: PayloadAction<StochasticDashboardState["dashboardData"]>,
    ) => {
      state.dashboardData = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setRefreshing: (state, action: PayloadAction<boolean>) => {
      state.refreshing = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
  },
});

export const { setChartsData, setLoading, setRefreshing, setError } =
  stochasticDashboardSlice.actions;

export const fetchStochasticDashboardChartsDataAction =
  (requestData: FetchDashboardDataRequest): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(setLoading(true));
    try {
      const response: FetchDashboardDataResponse =
        await fetchStochasticDashboardData(requestData);
      dispatch(setChartsData(response.data));
    } catch (error) {
      if (error instanceof Error) {
        dispatch(setError(error.message));
      } else {
        dispatch(setError("Failed to fetch stochastic dashboard data."));
      }
    } finally {
      dispatch(setLoading(false));
    }
  };

export const refreshStochasticDashboardDataAction =
  (intacctId: string): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(setRefreshing(true));
    dispatch(setError(null));
    try {
      await refreshStochasticDashboardData(intacctId);
    } catch (error) {
      if (error instanceof Error) {
        dispatch(setError(error.message));
      } else {
        dispatch(setError("Failed to refresh stochastic dashboard data."));
      }
      throw error;
    } finally {
      dispatch(setRefreshing(false));
    }
  };

export default stochasticDashboardSlice.reducer;
